// @givmo/compliance — a filing whose rule was updated, on its own panel (CCM-OPS1-REPIN; D-CCM-51,
// frames 103 and 104).
//
// A filing on the calendar stays pinned to the rule version it was confirmed against. When Givmo's
// rule is updated, the rules sync flags the filing and nothing else moves: never a silent rebind.
// The panel says so with N36, opens N37's comparison (`compliance-rule-change.ts`), and offers N38's
// "Use the updated rule" to someone who can make changes. That act is the organization's own,
// attributed: it moves the filing to the version the comparison showed, clears the flag and works
// its dates out again, keeping its history. N39 then says who moved it and when, read off the
// filing's own log (the newest `rule_version_repinned` event).
//
// The comparison says any change in approved words (D-CCM-59's U1). Where it can't say all of it (a
// dated change whose date isn't served), N36 stands alone, with no comparison and no act, as it does
// where the rule has no current version (U11). A filing whose tracking stopped shows no notice: it
// shows no dates either (U13).

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, testIdProp, toast } from '@givmo/design-system';
import { useCompliance } from './compliance-context';
import {
  decodeObligationDetail,
  displayComplianceDate,
  displayLocalDate,
  serverMessage,
  type ComplianceObligation,
} from './compliance-decode';
import { comparisonOf, decodeRuleChange, ruleUpdatedDay } from './compliance-rule-change';
import { nameIfKnown } from './review-model';
import { TextAction, WhatChanged } from './review-parts';
import {
  HIDE,
  SEE_WHAT_CHANGED,
  USE_UPDATED_RULE,
  thisRuleUpdatedOn,
  updatedToRule,
  updatedToRuleThroughAi,
} from './review-words';

const PREFIX = 'compliance-filing-panel';

/** The comparison's read, shared by the panel, its rule view and the review's change card. */
export function ruleChangeQueryKey(orgRef: string | null, obligationId: string) {
  return ['compliance', 'rule-change', orgRef, obligationId] as const;
}

/**
 * The filing's comparison, read only while a rule change flags it and its tracking hasn't stopped.
 * `pending` holds the notice until the read settles: N36's day is the version's own.
 */
export function useRuleChange(obligation: ComplianceObligation) {
  const { orgRef, operations } = useCompliance();
  const flagged =
    obligation.ruleChangeFlaggedOn !== undefined && obligation.trackingStoppedAt === undefined;
  const read = useQuery({
    queryKey: ruleChangeQueryKey(orgRef, obligation.obligationId),
    queryFn: () => operations.getObligationRuleChange(orgRef ?? '', obligation.obligationId),
    enabled: orgRef !== null && flagged,
  });
  const change = decodeRuleChange(read.data);
  return {
    flagged,
    pending: flagged && read.isPending,
    change,
    comparison: comparisonOf(change),
    day: flagged ? ruleUpdatedDay(change, obligation.ruleChangeFlaggedOn) : undefined,
  };
}

/** N36, with N37's comparison and N38's act where approved words say what changed (frame 103). */
export function RuleChangeNotice({
  obligation,
  canWrite,
}: {
  obligation: ComplianceObligation;
  canWrite: boolean;
}) {
  const { orgRef, operations } = useCompliance();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const { flagged, pending, comparison, day } = useRuleChange(obligation);
  const repin = useMutation({
    mutationFn: (ruleVersionId: string) =>
      operations.repinObligation(orgRef ?? '', obligation.obligationId, {
        rule_version_id: ruleVersionId,
      }),
    // The filing moved: its dates, its log and the flag are read again (the notice then goes).
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['compliance'] }),
    // Refused (the rule changed again, or the filing left the calendar): the served line, and the
    // comparison read again, so what the person sees is what the rule says now.
    onError: (cause) => {
      toast.error(serverMessage(cause, 'We couldn’t record that. Please try again.'));
      void queryClient.invalidateQueries({
        queryKey: ruleChangeQueryKey(orgRef, obligation.obligationId),
      });
    },
  });
  if (!flagged || pending || day === undefined) return null;
  return (
    <div
      className="flex flex-col gap-12 rounded-lg border border-border-subtle bg-surface-sunken px-16 py-12"
      {...testIdProp(`${PREFIX}-rule-changed`)}
    >
      <p className="text-s13 leading-h15 text-text">
        {thisRuleUpdatedOn(day)}
        {comparison !== undefined && (
          <>
            {' '}
            <TextAction
              onClick={() => setOpen((value) => !value)}
              testId={`${PREFIX}-rule-change-see`}
            >
              {open ? HIDE : SEE_WHAT_CHANGED}
            </TextAction>
          </>
        )}
      </p>
      {open && comparison !== undefined && (
        // N38's line says what the act does, so it shows only with the act (U8: someone who can only
        // view would read it as done).
        <WhatChanged
          comparison={comparison}
          layout="stacked"
          repinLine={canWrite}
          testId={`${PREFIX}-rule-change`}
        >
          {canWrite && (
            <div className="flex flex-wrap gap-8">
              <Button
                appearance="saas"
                tone="primary"
                size="sm"
                loading={repin.isPending}
                onClick={() => repin.mutate(comparison.currentRuleVersionId)}
                testId={`${PREFIX}-rule-change-use`}
              >
                {USE_UPDATED_RULE}
              </Button>
            </div>
          )}
        </WhatChanged>
      )}
    </div>
  );
}

/**
 * N39's words (frame 104): who moved the filing to the updated rule, and when, from its newest
 * `rule_version_repinned` event; else none. It stays as the filing's latest version change (the
 * GO's Q4), as N41 stays. Made through the person's connected AI (the event's `channel` says
 * `connector`), it says so, in N56's form (D-CCM-96, frame A2).
 */
export function useRepinnedLine(obligation: ComplianceObligation): string | undefined {
  const { orgRef, operations, personName } = useCompliance();
  // The panel's own read of the filing, under its key: its log.
  const detail = useQuery({
    queryKey: ['compliance', 'obligation', orgRef, obligation.obligationId],
    queryFn: () => operations.getObligation(orgRef ?? '', obligation.obligationId),
    enabled: orgRef !== null,
  });
  const repinned = decodeObligationDetail(detail.data)
    .events.filter((event) => event.eventType === 'rule_version_repinned')
    .at(-1);
  const ruleDay = repinned?.detail.to_rule_version_loaded_on;
  if (repinned === undefined || typeof ruleDay !== 'string' || ruleDay.length === 0)
    return undefined;
  if (repinned.detail.channel === 'connector') {
    return updatedToRuleThroughAi(
      displayComplianceDate(ruleDay),
      repinned.actorUserId !== undefined ? personName?.(repinned.actorUserId) : undefined,
      displayLocalDate(repinned.occurredAt),
    );
  }
  return updatedToRule(
    displayComplianceDate(ruleDay),
    nameIfKnown((userId) => personName?.(userId), repinned.actorUserId),
    displayLocalDate(repinned.occurredAt),
  );
}
